import {
  customerActivityObservationResponseSchema,
  journeyScreen,
  type ClientErrorCode,
  type CustomerActivityObservationCommand,
} from "@lifescapsule/contracts";

function eventId(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return "10000000-1000-4000-8000-100000000000".replace(/[018]/gu, (character) =>
    (
      Number(character) ^
      (crypto.getRandomValues(new Uint8Array(1))[0]! & (15 >> (Number(character) / 4)))
    ).toString(16),
  );
}

export async function observeCustomerActivity(
  idToken: string,
  command: CustomerActivityObservationCommand,
): Promise<boolean> {
  try {
    const response = await fetch("/api/v1/operations/observations", {
      method: "POST",
      headers: {
        authorization: `Bearer ${idToken}`,
        "content-type": "application/json",
        "idempotency-key": command.eventId,
      },
      credentials: "omit",
      cache: "no-store",
      keepalive: true,
      body: JSON.stringify(command),
    });
    if (!response.ok) return false;
    return customerActivityObservationResponseSchema.parse(await response.json()).data.accepted;
  } catch {
    return false;
  }
}

export function observeAppSession(idToken: string): Promise<boolean> {
  return observeCustomerActivity(idToken, {
    eventId: eventId(),
    eventType: "APP_SESSION_ESTABLISHED",
  });
}

export function observeSignOut(idToken: string): Promise<boolean> {
  return observeCustomerActivity(idToken, {
    eventId: eventId(),
    eventType: "CLIENT_SIGN_OUT_SELECTED",
  });
}

let reportingToken: string | undefined;
const reportedErrors = new Set<string>();

/** The signed-in session, so a page that fails can say so (pilot watch, 2026-10-17). */
export function setClientErrorReportingToken(idToken: string | undefined) {
  reportingToken = idToken;
}

/**
 * Tells the team a page failed for a signed-in customer: a code and the screen it was on, with
 * every id replaced. Never the error's message or stack, which can hold what they typed. At most
 * five a page load, each code and screen once.
 */
export function reportClientError(code: ClientErrorCode, path = window.location.pathname) {
  const screen = journeyScreen(path);
  if (!reportingToken || !screen) return;
  const key = `${code}|${screen}`;
  if (reportedErrors.has(key) || reportedErrors.size >= 5) return;
  reportedErrors.add(key);
  void observeCustomerActivity(reportingToken, {
    eventId: eventId(),
    eventType: "CLIENT_ERROR",
    code,
    screen,
  });
}

/** Typed LC-97 handoff; Studio calls this once when a member opens an authorized book session. */
export function observeStudioSession(idToken: string, bookId: string): Promise<boolean> {
  return observeCustomerActivity(idToken, {
    eventId: eventId(),
    eventType: "STUDIO_SESSION_STARTED",
    bookId,
  });
}
